import { useTranslation } from '../../../i18n';
import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    message,
    Popconfirm,
    Tag,
    Alert
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    FileOutlined,
    OrderedListOutlined
} from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';

const IndexFileManager = ({ vhostName }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (vhostName) {
            loadData();
        }
    }, [vhostName]);

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await hostSharedService.getIndexFile(vhostName);
            if (response.success) {
                const payload = response.data;
                let items = [];

                if (Array.isArray(payload)) {
                    items = payload;
                } else if (payload) {
                    if (Array.isArray(payload.items)) {
                        items = payload.items;
                    } else if (Array.isArray(payload.files)) {
                        items = payload.files;
                    } else if (Array.isArray(payload.data)) {
                        items = payload.data;
                    }
                }

                setDataSource(items.map((item, index) => {
                    const name = typeof item === 'string'
                        ? item
                        : item.file || item.name || item.value || '';

                    return {
                        file: name,
                        order: index + 1,
                        key: `${name || 'index'}-${index}`
                    };
                }));
            } else {
                message.error(response.error || t('Failed to load default index file list'));
                setDataSource([]);
            }
        } catch (error) {
            message.error(t('Failed to load: {{message}}', { message: error.message }));
            setDataSource([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        form.resetFields();
        setModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const response = await hostSharedService.addIndexFile(vhostName, values.file);
            if (response.success) {
                message.success(t('Added successfully'));
                setModalVisible(false);
                loadData();
            } else {
                message.error(response.error || t('Failed to add'));
                setDataSource([]);
            }
        } catch (error) {
            if (error.errorFields) {
                return;
            }
            message.error(t('Failed to add: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (file) => {
        setLoading(true);
        try {
            const response = await hostSharedService.deleteIndexFile(vhostName, file);
            if (response.success) {
                message.success(t('Deleted successfully'));
                loadData();
            } else {
                message.error(response.error || t('Deletion failed'));
                setDataSource([]);
            }
        } catch (error) {
            message.error(t('Deletion failed: {{message}}', { message: error.message }));
            setDataSource([]);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: t('Priority'),
            dataIndex: 'order',
            key: 'order',
            width: 100,
            render: (text) => <Tag color="blue">#{text}</Tag>,
        },
        {
            title: t('File name'),
            dataIndex: 'file',
            key: 'file',
            render: (text) => (
                <Space>
                    <FileOutlined />
                    <strong>{text}</strong>
                </Space>
            ),
        },
        {
            title: t('Actions'),
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Popconfirm
                    title={t('Delete this default index page?')}
                    onConfirm={() => handleDelete(record.file)}
                    okText={t('OK')}
                    cancelText={t('Cancel')}
                >
                    <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                        {t('Delete')}
                    </Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <Card
            title={
                <Space>
                    <OrderedListOutlined />
                    {t('Default index page management')}
                </Space>
            }
            extra={
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                >
                    {t('Add index page')}
                </Button>
            }
        >
            <Alert
                message={t('Default index page instructions')}
                description={t('Set the default files to display when a directory is accessed. Files are checked from top to bottom in priority order. Common examples include index.html, index.php, and default.html')}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <Table
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={false}
            />

            <Modal
                title={t('Add default index page')}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                confirmLoading={loading}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label={t('File name')}
                        name="file"
                        rules={[
                            { required: true, message: t('Please enter a filename') },
                            { pattern: /^[\w\.\-]+$/, message: t('Only letters, numbers, dots, underscores, and hyphens are allowed') }
                        ]}
                    >
                        <Input placeholder={t('E.g. index.html')} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default IndexFileManager;
